import { deleteApp, type FirebaseOptions } from 'firebase/app'
import { onAuthStateChanged } from 'firebase/auth'
import { navigate } from 'wouter-preact/use-browser-location'
import { getToken } from 'firebase/app-check'

import { application } from 'state/application'
import { emitSystemNotification } from 'state/system_notification'

import { getUserInformation } from '../user_information'
import { initializeFirebase } from './initialize_firebase'
import { updateAuthState, updateFirebaseAppCheckState } from './state'
import { AuthState } from './types'
import { signInWithLinkConfirmation } from './sign_in_passwordless_confirmation'
import { signInWithOauth2Confirmation } from './sign_in_with_oauth2_confirmation'

/**
 * Sign in into our backed, at this step the backend will set the
 * auth cookie for the following requests. If ok, it will return the
 * user information and set it
 * @returns true sign in was ok, otherwise false
 */
const signIn = async (
	idToken: string,
	appCheckToken: string
): Promise<boolean> => {
	const { error, status } = await application
		.peek()
		.forManagingAccount()
		.signIn(idToken, appCheckToken)

	if (error && status !== 404) {
		emitSystemNotification({
			error,
			display: 'dialog'
		})

		updateAuthState(() => AuthState.unauthenticated)
		return false
	}

	if (status === 404) {
		updateAuthState(() => AuthState.signingUp)
		navigate('/profile')
		return false
	}

	return true
}

/**
 * Gets the user information from the backend, if got data
 * set the userInformationState and the authState to Authenticated
 * if user is not authenticated, initialize firebase auth, app check, and
 * setups firebase listener to check for auth state changes from part of
 * firebase
 *
 * @param tenantId default tenantId to which the users will sign in/up against
 * @param config firebase configuration
 */
export const initializeAuth = async (
	tenantId: string,
	config: FirebaseOptions
) => {
	// 1. Get user information
	if (await getUserInformation()) {
		updateAuthState(() => AuthState.authenticated)
		return
	}

	// 2. User is NOT authenticated
	// Initialize firebase app and start
	// auth process
	const { auth, app, appCheck } = initializeFirebase(tenantId, config)

	// Check if we are in an authentication process
	await signInWithLinkConfirmation()
	await signInWithOauth2Confirmation()

	// 3. Setup firebase auth state listener
	const disposeUserListener = onAuthStateChanged(auth, async user => {
		if (user) {
			const appCheckToken = (await getToken(appCheck)).token

			// If for some reason here was an error
			// in the getUserInformation into our backend let's
			// sign out so user can start the process again
			// the error will emitted within the signIn
			// call getUserInformation
			const signed = await signIn(await user.getIdToken(), appCheckToken)

			// If signed was success:
			// - Remove the firebase listener for authState
			// - Delete the firebase app instance
			// - Update our state to authenticated
			// - Replace old auth routes and navigate to home
			// - Remove the firebase app check
			if (signed) {
				await getUserInformation()
				disposeUserListener()
				updateFirebaseAppCheckState(() => null)
				updateAuthState(() => AuthState.authenticated)
				navigate('/', { replace: true })
				deleteApp(app)
			}

			return
		}

		updateAuthState(() => AuthState.unauthenticated)
	})
}
